<template>
  <v-container>
    <v-card>
      <v-toolbar>
        <v-toolbar-title>{{
          t("campaign.create.title", { merchant: currentMerchant.name })
        }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <img :src="currentMerchant.image.src" height="50" />
      </v-toolbar>

      <v-container v-if="currentMerchant.uuid">
        <div class="text-body-2">{{ t("campaign.create.intro") }}</div>
        <div class="pt-3">
          <div v-for="type in availableTypes" :key="type.value">
            <v-row v-if="!type.isSub || (type.isSub && showCouponSub)">
              <v-col cols="4" :class="{ 'pl-12': type.isSub }">
                <v-btn
                  color="primary"
                  elevation="5"
                  rounded
                  size="x-large"
                  block
                  class="type-btn"
                  @click="doCreate(type.value)"
                >
                  <v-icon start>{{ t("campaign.icon." + type.value) }}</v-icon>
                  {{ t("campaign.type." + type.value) }}
                </v-btn>
              </v-col>
              <v-col cols="8" align-self="center">
                {{ t("campaign.create." + type.hint) }}
              </v-col>
            </v-row>
          </div>
        </div>
      </v-container>
      <v-container v-else>
        <v-btn color="error" @click="showSelectMerchant()">
          {{ t("campaign.create.btn_select_merchant") }}
        </v-btn>
      </v-container>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, computed, onBeforeMount, getCurrentInstance } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useMerchantStore } from '@/stores/merchant'
import { createCampaign } from '@/api/campaign.js'

const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()
const merchantStore = useMerchantStore()
const { $alert } = getCurrentInstance().appContext.config.globalProperties

const showCouponSub = ref(false)
const availableTypes = ref([
  {
    value: 'sweepstake/standard',
    hint: 'hint_sweepstake_standard',
  },
  {
    value: 'coupon/free',
    hint: 'hint_coupon_free',
  },
  {
    value: 'coupon',
    hint: 'hint_coupon',
    hasSub: true,
  },
  {
    value: 'coupon/percent',
    hint: 'hint_coupon_percent',
    isSub: true,
  },
  {
    value: 'coupon/absolute',
    hint: 'hint_coupon_absolute',
    isSub: true,
  },
  {
    value: 'coupon/offer',
    hint: 'hint_coupon_offer',
    isSub: true,
  },
  {
    value: 'coupon/m_for_n',
    hint: 'hint_coupon_m_for_n',
    isSub: true,
  },
])

const currentMerchant = computed(() => authStore.currentMerchant)
const isAdmin = computed(() => authStore.isAdmin)
const isAgency = computed(() => authStore.isAgency)

const doCreate = async (type) => {
  if (type == 'coupon') {
    showCouponSub.value = !showCouponSub.value
    return
  }
  const selectedType = availableTypes.value.find((t) => t.value === type)
  if (selectedType) {
    const res = await createCampaign({
      merchant: currentMerchant.value.uuid,
      type: type,
    })
    const routeName = res.campaign.type.split('/')[0] + '-benefit'
    $alert({
      message: t('campaign.create.msg_created', {
        ident: res.campaign.uuid,
      }),
    })
    router.push({
      name: routeName,
      params: {
        merchantIdent: currentMerchant.value.uuid,
        campaignIdent: res.campaign.uuid,
      },
    })
  } else {
    alert(type + ' does not exist')
  }
}

const showSelectMerchant = () => {
  merchantStore.showMerchantSelectModal({
    show: true,
    closable: false,
  })
}

onBeforeMount(() => {
  if (isAgency.value || isAdmin.value) {
    availableTypes.value.push({
      value: 'tip/generic',
      hint: 'hint_tip_generic',
    })
  }
})
</script>

<style scoped>
.campaignSelector {
  cursor: pointer;
}
.type-btn {
  height: auto !important;
  min-height: 44px;
  padding-top: 8px;
  padding-bottom: 8px;
}
.type-btn :deep(.v-btn__content) {
  white-space: normal;
  text-align: left;
  justify-content: flex-start;
  width: 100%;
}
</style>
